// Central registry of the platform's apps + the shared demo-clinic entry, used by
// the landing hub and the "for clinics" page.
//
// ⚠️ These are RESOLVED AT RUNTIME from the hostname this page was served on — never hardcoded.
// They used to be staging URLs written into the source, which made deploying to production a code
// change: the production site would have shipped with every card, demo link and sign-in button
// pointing back at staging. The links resolve, so nothing looks broken; it is simply the wrong
// environment, and a customer finds it before we do. See `appUrls.ts` in shared-ui.
import { appOrigin, siteOrigin, withLang, withTheme } from '@easydent/shared-ui';

export const CLINIC_APP_URL = appOrigin('clinic');
export const PATIENT_APP_URL = appOrigin('patient');
export const IMAGING_APP_URL = appOrigin('imaging');
// Singular, matching the DNS record and the Caddy vhost — and every other portal here. The plural
// spelling resolved nowhere, so the Supplier portal card and its demo login both led to a dead host.
export const SUPPLIER_APP_URL = appOrigin('supplier');

/** Public site origin — used for canonical URLs, Open Graph and schema.org (no trailing slash). */
export const SITE_URL = siteOrigin();

export const DEMO_PASSWORD = 'EasyDentDemo!2026';

export interface DemoRole {
  key: string;
  label: string;
  email: string;
  desc: string;
}

/**
 * ⚠️ `label` and `desc` are i18n KEYS, not text.
 *
 * A module-level constant is evaluated once, before i18n has a language and outside any component,
 * so `t()` here would freeze whatever loaded first — the same trap the operator app's nav comment
 * warns about. The two pages that render these resolve them, which is also what makes a language
 * switch re-render them.
 */
export const DEMO_ROLES: DemoRole[] = [
  { key: 'admin', label: 'demo.roles.admin', email: 'admin@demo.easydent.ro', desc: 'demo.roles.adminDesc' },
  { key: 'doctor', label: 'demo.roles.doctor', email: 'doctor@demo.easydent.ro', desc: 'demo.roles.doctorDesc' },
  { key: 'receptionist', label: 'demo.roles.receptionist', email: 'receptionist@demo.easydent.ro', desc: 'demo.roles.receptionistDesc' },
];

/** Deep-link into the clinic app's auto-login demo sandbox for a given role. */
export const demoUrl = (role: string) => withTheme(withLang(`${CLINIC_APP_URL}/demo?role=${role}`));

/** Real self-service sign-up: creates a brand-new clinic (tenant) with its own admin account. */
export const registerUrl = () => withTheme(withLang(`${CLINIC_APP_URL}/register`));

/** Deep-link into the patient portal's auto-login demo (signs in as the shared demo patient). */
export const patientDemoUrl = () => withTheme(withLang(`${PATIENT_APP_URL}/demo`));

/** Deep-link into the imaging portal's auto-login demo (signs in as the shared demo X-ray centre). */
export const imagingDemoUrl = () => withTheme(withLang(`${IMAGING_APP_URL}/demo`));

/** Imaging-portal sign-in, carrying the current UI language. */
export const imagingSignInUrl = () => withTheme(withLang(IMAGING_APP_URL));

/** Deep-link into the supplier portal's auto-login demo (signs in as the shared demo advertiser). */
export const supplierDemoUrl = () => withTheme(withLang(`${SUPPLIER_APP_URL}/demo`));

/** Supplier-portal sign-in, carrying the current UI language. */
export const supplierSignInUrl = () => withTheme(withLang(SUPPLIER_APP_URL));

/** Clinic-app sign-in, carrying the current UI language. */
export const clinicSignInUrl = () => withTheme(withLang(CLINIC_APP_URL));

/** Patient-portal sign-in, carrying the current UI language. */
export const patientSignInUrl = () => withTheme(withLang(PATIENT_APP_URL));

// The APPS/AppLink registry that used to live here was DELETED on 2026-09-22: nothing imported it.
// The landing hub and the for-clinics page each build their own card list inline with t(), so the
// registry had drifted into thirteen English strings that no page could ever render — and which
// showed up as untranslated UI text in an audit precisely because they looked like UI text.
// The comment at the top of this file still claimed it drove both pages.
